import React, { useEffect, useImperativeHandle, useState, useRef } from 'react';
import {
    Typography,
    Input,
    FormLabel,
    FormControl,
    Link as MuiLink,
    FormHelperText,
    Box,
    CircularProgress as JoyCircularProgress,
    Button,
    Card,
    CardContent,
    Chip,
    Stack,
    Divider,
    RadioGroup,
    Radio,
    Select,
    Modal,
    ModalDialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/joy';
import Option from '@mui/joy/Option';
import { GoogleMap, MarkerF, useJsApiLoader } from '@react-google-maps/api';

import ApiService from "../../../axios/Bookings-Axios";

// New pricing constants
const BASE_PRICE = 750;
const EXTRA_FACE_CHARGE = 75;
const SQM_THRESHOLD = 150;
const SQM_RATE = 2;

// Pre-booking question pricing
export const CONSERVATORY_SIZE_PRICES: Record<PreBookingSize, number> = {
    small: 125,
    medium: 150,
    large: 200,
};
export const GARAGE_SIZE_PRICES: Record<PreBookingSize, number> = {
    small: 125,
    medium: 150,
    large: 200,
};
// Cherry picker surcharge for 3+ storey houses: £650 per day of work
export const CHERRY_PICKER_RATE_PER_DAY = 650;
export const CHERRY_PICKER_MIN_STORIES = 3;

export type YesNo = 'yes' | 'no' | null;
export type PreBookingSize = 'small' | 'medium' | 'large';

export interface PreBookingAnswers {
    conservatoryIncluded: YesNo;
    conservatorySize: PreBookingSize | null;
    garageIncluded: YesNo;
    garageSize: PreBookingSize | null;
    asbestosRoof: YesNo;
    /**
     * How many storeys the property has. null = the sales rep hasn't
     * answered yet — they must type an actual number before the lead can
     * be converted to a booking (no silent default). 3+ requires a cherry picker.
     */
    stories: number | null;
    /** Whether the customer has an outside tap for water access. */
    hasOutsideTap: YesNo;
    /** Only relevant when hasOutsideTap === 'no'. Whether a neighbour (either side) has directly confirmed permission to use theirs. */
    neighbourHasOutsideTap: YesNo;
    /** Whether the customer has an outside electrical point we can use. */
    hasOutsideElectricalPoint: YesNo;
}

export const createEmptyPreBookingAnswers = (): PreBookingAnswers => ({
    conservatoryIncluded: 'no',
    conservatorySize: null,
    garageIncluded: 'no',
    garageSize: null,
    asbestosRoof: null,
    stories: null,
    hasOutsideTap: null,
    neighbourHasOutsideTap: null,
    hasOutsideElectricalPoint: null,
});

export const needsCherryPicker = (answers: PreBookingAnswers): boolean =>
    (answers.stories ?? 0) >= CHERRY_PICKER_MIN_STORIES;

/**
 * Asbestos roofs are a hard blocker — we can't clean them. Used by the
 * Convert To Booking flow to disable conversion and surface a message.
 */
export const isAsbestosBlocked = (answers: PreBookingAnswers): boolean =>
    answers.asbestosRoof === 'yes';

export const ASBESTOS_BLOCK_MESSAGE =
    "We can't go ahead with this booking because the roof contains asbestos. " +
    "Asbestos roofs can't be safely cleaned by our vans — please let the customer know.";

/**
 * No water available from the customer AND no neighbour tap to fall back on —
 * we can't complete the job without water. Also a hard blocker.
 */
export const isNoWaterBlocked = (answers: PreBookingAnswers): boolean =>
    answers.hasOutsideTap === 'no' && answers.neighbourHasOutsideTap === 'no';

export const NO_WATER_BLOCK_MESSAGE =
    "We can't go ahead with this booking — there's no outside tap at the property " +
    "and no neighbour's tap available for us to use. Our vans rely on on-site water " +
    "to clean roofs, so please let the customer know we can't proceed without water access.";

/** Return a user-facing reason the lead can't be converted, or null if it can. */
export const getBookingBlockMessage = (answers: PreBookingAnswers): string | null => {
    if (isAsbestosBlocked(answers)) return ASBESTOS_BLOCK_MESSAGE;
    if (isNoWaterBlocked(answers)) return NO_WATER_BLOCK_MESSAGE;
    return null;
};

export const getCherryPickerExtra = (answers: PreBookingAnswers, daysToComplete: number): number => {
    if (!needsCherryPicker(answers)) return 0;
    const days = Math.max(1, Math.round(daysToComplete || 0));
    return CHERRY_PICKER_RATE_PER_DAY * days;
};

export const getPreBookingExtraPrice = (
    answers: PreBookingAnswers,
    daysToComplete: number = 0,
): number => {
    let extra = 0;
    if (answers.conservatoryIncluded === 'yes' && answers.conservatorySize) {
        extra += CONSERVATORY_SIZE_PRICES[answers.conservatorySize];
    }
    if (answers.garageIncluded === 'yes' && answers.garageSize) {
        extra += GARAGE_SIZE_PRICES[answers.garageSize];
    }
    extra += getCherryPickerExtra(answers, daysToComplete);
    return extra;
};

export const validatePreBookingAnswers = (answers: PreBookingAnswers): string[] => {
    const missing: string[] = [];
    if (answers.conservatoryIncluded === 'yes' && !answers.conservatorySize) missing.push('Conservatory Size');
    if (answers.garageIncluded === 'yes' && !answers.garageSize) missing.push('External Garage Size');
    if (answers.asbestosRoof === null) missing.push('Asbestos Roof');
    if (answers.stories === null || answers.stories <= 0) missing.push('Number of Storeys');
    if (answers.hasOutsideTap === null) missing.push('Outside Tap');
    if (answers.hasOutsideTap === 'no' && answers.neighbourHasOutsideTap === null) missing.push("Neighbour's Outside Tap");
    if (answers.hasOutsideElectricalPoint === null) missing.push('Outside Electrical Point');
    return missing;
};

const formatSize = (size: PreBookingSize | null): string => {
    if (!size) return '';
    return size.charAt(0).toUpperCase() + size.slice(1);
};

const yesNoToBool = (v: YesNo): boolean | null => (v === 'yes' ? true : v === 'no' ? false : null);
const boolToYesNo = (v: boolean | null | undefined): YesNo => (v === true ? 'yes' : v === false ? 'no' : null);

/**
 * Build the payload keys to persist the pre-booking answers on the Lead.
 * Matches the Django model fields added in bookings migrations 0159 and 0160.
 */
export const preBookingAnswersToPayload = (answers: PreBookingAnswers) => ({
    pre_booking_garage_included: yesNoToBool(answers.garageIncluded),
    pre_booking_garage_size: answers.garageSize,
    pre_booking_conservatory_included: yesNoToBool(answers.conservatoryIncluded),
    pre_booking_conservatory_size: answers.conservatorySize,
    pre_booking_asbestos_roof: yesNoToBool(answers.asbestosRoof),
    pre_booking_stories: answers.stories,
    pre_booking_has_outside_tap: yesNoToBool(answers.hasOutsideTap),
    pre_booking_neighbour_has_outside_tap: yesNoToBool(answers.neighbourHasOutsideTap),
    pre_booking_has_outside_electrical_point: yesNoToBool(answers.hasOutsideElectricalPoint),
});

/** Read pre-booking answers from a lead returned by the backend. */
export const preBookingAnswersFromLead = (lead: any): PreBookingAnswers => ({
    garageIncluded: boolToYesNo(lead?.pre_booking_garage_included) ?? 'no',
    garageSize: (lead?.pre_booking_garage_size ?? null) as PreBookingSize | null,
    conservatoryIncluded: boolToYesNo(lead?.pre_booking_conservatory_included) ?? 'no',
    conservatorySize: (lead?.pre_booking_conservatory_size ?? null) as PreBookingSize | null,
    asbestosRoof: boolToYesNo(lead?.pre_booking_asbestos_roof),
    stories: typeof lead?.pre_booking_stories === 'number' ? lead.pre_booking_stories : null,
    hasOutsideTap: boolToYesNo(lead?.pre_booking_has_outside_tap),
    neighbourHasOutsideTap: boolToYesNo(lead?.pre_booking_neighbour_has_outside_tap),
    hasOutsideElectricalPoint: boolToYesNo(lead?.pre_booking_has_outside_electrical_point),
});

/**
 * Banner shown at the very top of the booking notes when a cherry picker
 * is required. Returns an empty string when not applicable so callers can
 * safely prepend unconditionally.
 */
export const buildCherryPickerBanner = (
    answers: PreBookingAnswers,
    daysToComplete: number = 0,
): string => {
    if (!needsCherryPicker(answers)) return '';
    const days = Math.max(1, Math.round(daysToComplete || 0));
    const cost = CHERRY_PICKER_RATE_PER_DAY * days;
    const stories = answers.stories ?? 0;
    return (
        `⚠️ CHERRY PICKER NEEDED ⚠️\n` +
        `${stories}-storey property — hire cherry picker (£${CHERRY_PICKER_RATE_PER_DAY}/day x ${days} day${days === 1 ? '' : 's'} = £${cost})`
    );
};

export const buildPreBookingNotesBlock = (
    answers: PreBookingAnswers,
    daysToComplete: number = 0,
): string => {
    const lines: string[] = ['--- Pre-Booking Questions ---'];

    const stories = answers.stories;
    if (stories === null) {
        lines.push('Stories: Unknown');
    } else if (needsCherryPicker(answers)) {
        const days = Math.max(1, Math.round(daysToComplete || 0));
        const cherryPickerCost = CHERRY_PICKER_RATE_PER_DAY * days;
        lines.push(
            `Stories: ${stories} — CHERRY PICKER REQUIRED (£${CHERRY_PICKER_RATE_PER_DAY}/day x ${days} day${days === 1 ? '' : 's'} = £${cherryPickerCost})`
        );
    } else {
        lines.push(`Stories: ${stories}`);
    }

    if (answers.conservatoryIncluded === 'yes' && answers.conservatorySize) {
        lines.push(`Conservatory Included (${formatSize(answers.conservatorySize)}) - £${CONSERVATORY_SIZE_PRICES[answers.conservatorySize]}`);
    } else {
        lines.push('Conservatory NOT included');
    }

    if (answers.garageIncluded === 'yes' && answers.garageSize) {
        lines.push(`External Garage Included (${formatSize(answers.garageSize)}) - £${GARAGE_SIZE_PRICES[answers.garageSize]}`);
    } else {
        lines.push('External Garage NOT included');
    }

    lines.push(`Asbestos Roof: ${answers.asbestosRoof === 'yes' ? 'Yes' : 'No'}`);

    if (answers.hasOutsideTap === 'yes') {
        lines.push('Outside Tap: Yes (customer has water access on-site)');
    } else if (answers.hasOutsideTap === 'no') {
        if (answers.neighbourHasOutsideTap === 'yes') {
            lines.push(
                'Outside Tap: NO - neighbour tap may be available, but the neighbour must directly confirm permission before the booking is confirmed. Client confirmation alone is not enough because the neighbour would pay for any additional water usage.'
            );
        } else if (answers.neighbourHasOutsideTap === 'no') {
            lines.push('Outside Tap: NO (neighbour also refused) - bring water from the base');
        } else {
            lines.push('Outside Tap: NO - bring water from the base');
        }
    } else {
        lines.push('Outside Tap: Unknown');
    }

    if (answers.hasOutsideElectricalPoint === 'yes') {
        lines.push('Outside Electrical Point: Yes');
    } else if (answers.hasOutsideElectricalPoint === 'no') {
        lines.push('Outside Electrical Point: NO — van team may need an alternative power source');
    } else {
        lines.push('Outside Electrical Point: Unknown');
    }

    return lines.join('\n');
};

/**
 * Wrap existing booking notes with the cherry-picker banner at the top and the
 * pre-booking question block at the bottom. Shared by the lead->booking
 * conversion so the notes are formatted consistently.
 */
export const composeBookingNotes = (
    existingNotes: string,
    answers: PreBookingAnswers,
    daysToComplete: number = 0,
): string => {
    const banner = buildCherryPickerBanner(answers, daysToComplete);
    const preBookingBlock = buildPreBookingNotesBlock(answers, daysToComplete);
    const trimmedExisting = (existingNotes || '').trim();

    const sections: string[] = [];
    if (banner) sections.push(banner);
    if (trimmedExisting) sections.push(trimmedExisting);
    sections.push(preBookingBlock);
    return sections.join('\n\n');
};

interface CustomerData {
    id: number;
    full_name: string;
    street_address: string;
    postcode: string;
    franchise_postcode?: {
        affluence_multiplier?: number;
    }
}
interface ConfirmCustomersRoofTabProps {
    roofData: {
        id: number;
        roof_url: string | null;
        square_meter: number;
        face_count: number;
        pin_latitude?: number | null;
        pin_longitude?: number | null;
        pin_confirmed_at?: string | null;
    } | null;
    customerData: any;
    currentPrice: number;
    quoteSent?: boolean;
    roofPriceManuallyEdited?: boolean;
    onPriceChange: (newPrice: string, source?: 'auto' | 'suggested' | 'manual') => void;
    onSuggestedPriceChange?: (newPrice: number) => void;
    onRoofUpdated?: (roof: any) => void;
    onPinConfirmationStateChange?: (state: RoofPinConfirmationState) => void;
    onPersistAnswers?: (answers: PreBookingAnswers) => void;
    squareMeter: string;
    onSquareMeterChange: (newSqm: string) => void;
    daysRange: { min: number; max: number } | null;
    answers: PreBookingAnswers;
    onAnswersChange: (next: PreBookingAnswers) => void;
    showValidation?: boolean;
    error: string | null;
    referralDiscountPercent?: number;
}

export interface ConfirmCustomersRoofTabHandle {
    openConfirmPinModal: () => void;
}

export interface RoofPinConfirmationState {
    isActive: boolean;
    canConfirm: boolean;
    isLoading: boolean;
    pinWasMoved: boolean;
}

interface RoofPricePreview {
    recommended_price?: number;
    original_recommended_price?: number;
    discount_percent?: number;
    discount_amount?: number;
    floor_price?: number;
    upper_price?: number;
    breakdown?: {
        base_price?: number;
        sqm_threshold?: number;
        sqm_rate?: number;
        sqm_adjustment?: number;
        extra_faces?: number;
        extra_face_charge?: number;
        extra_faces_charge?: number;
        conservatory_charge?: number;
        garage_charge?: number;
        cherry_picker_charge?: number;
        cherry_picker_rate_per_day?: number;
        cherry_picker_days?: number;
        price_multiplier?: number;
    };
}

const libraries: ('geometry' | 'maps')[] = ['geometry', 'maps'];

const ConfirmCustomersRoofTab = React.forwardRef<ConfirmCustomersRoofTabHandle, ConfirmCustomersRoofTabProps>(function ConfirmCustomersRoofTab({
                                                                                                                                                 roofData,
                                                                                                                                                 customerData,
                                                                                                                                                 currentPrice,
                                                                                                                                                 quoteSent = false,
                                                                                                                                                 roofPriceManuallyEdited = false,
                                                                                                                                                 onPriceChange,
                                                                                                                                                 onSuggestedPriceChange,
                                                                                                                                                 onRoofUpdated,
                                                                                                                                                 onPinConfirmationStateChange,
                                                                                                                                                 onPersistAnswers,
                                                                                                                                                 squareMeter,
                                                                                                                                                 daysRange,
                                                                                                                                                 onSquareMeterChange,
                                                                                                                                                 answers,
                                                                                                                                                 onAnswersChange,
                                                                                                                                                 showValidation = false,
                                                                                                                                                 error,
                                                                                                                                                 referralDiscountPercent = 0,
                                                                                                                                             }, ref) {

    const [localRoofData, setLocalRoofData] = useState(roofData);
    const effectiveRoofData = localRoofData || roofData;
    const faceCount = effectiveRoofData?.face_count || 0;
    const sqm = Number(squareMeter || 0);
    const multiplier = customerData?.franchise_postcode?.affluence_multiplier ?? 1.0;

    const conservatoryExtra = answers.conservatoryIncluded === 'yes' && answers.conservatorySize
        ? CONSERVATORY_SIZE_PRICES[answers.conservatorySize]
        : 0;
    const garageExtra = answers.garageIncluded === 'yes' && answers.garageSize
        ? GARAGE_SIZE_PRICES[answers.garageSize]
        : 0;

    // Days used for the cherry picker calculation. Mirrors the value the
    // BookingsDrawer is seeded with when Convert To Booking fires.
    const cherryPickerDays = Math.max(1, Math.round(daysRange?.min ?? 0) || 1);
    const cherryPickerRequired = needsCherryPicker(answers);
    const cherryPickerExtra = cherryPickerRequired ? CHERRY_PICKER_RATE_PER_DAY * cherryPickerDays : 0;
    const [backendPricePreview, setBackendPricePreview] = useState<RoofPricePreview | null>(null);
    const lastAutoSyncedPrice = useRef<number | null>(null);
    const [showPinConfirmation, setShowPinConfirmation] = useState(!quoteSent && !roofData?.pin_confirmed_at);
    const [pinPosition, setPinPosition] = useState<{ lat: number; lng: number } | null>(null);
    const [initialPinPosition, setInitialPinPosition] = useState<{ lat: number; lng: number } | null>(null);
    const [pinWasMoved, setPinWasMoved] = useState(false);
    const [isPinLoading, setIsPinLoading] = useState(false);
    const [pinError, setPinError] = useState<string | null>(null);
    const [pinWarning, setPinWarning] = useState<string | null>(null);
    const [confirmPinModalOpen, setConfirmPinModalOpen] = useState(false);
    const [isConfirmingPin, setIsConfirmingPin] = useState(false);

    const missingAnswers = validatePreBookingAnswers(answers);

    // Per-field "still missing" flags (used to highlight unanswered questions in red)
    const garageMissing = answers.garageIncluded === null;
    const garageSizeMissing = answers.garageIncluded === 'yes' && !answers.garageSize;
    const conservatoryMissing = answers.conservatoryIncluded === null;
    const conservatorySizeMissing = answers.conservatoryIncluded === 'yes' && !answers.conservatorySize;
    const asbestosMissing = answers.asbestosRoof === null;
    const outsideTapMissing = answers.hasOutsideTap === null;
    const neighbourTapMissing = answers.hasOutsideTap === 'no' && answers.neighbourHasOutsideTap === null;
    const electricalPointMissing = answers.hasOutsideElectricalPoint === null;
    const storiesMissing = answers.stories === null || answers.stories <= 0;

    const questionBoxSx = (hasError: boolean) => ({
        mb: 2,
        p: 2,
        border: '1px solid',
        borderColor: hasError ? 'danger.500' : 'divider',
        borderRadius: 'sm',
        bgcolor: hasError ? 'danger.softBg' : undefined,
    });

    const updateAnswer = <K extends keyof PreBookingAnswers>(key: K, value: PreBookingAnswers[K]) => {
        const next = { ...answers, [key]: value };
        onAnswersChange(next);
        onPersistAnswers?.(next);
    };

    useImperativeHandle(ref, () => ({
        openConfirmPinModal: () => {
            if (!showPinConfirmation || !pinPosition || isPinLoading || isConfirmingPin) return;
            setConfirmPinModalOpen(true);
        },
    }), [showPinConfirmation, pinPosition, isPinLoading, isConfirmingPin]);

    useEffect(() => {
        onPinConfirmationStateChange?.({
            isActive: showPinConfirmation,
            canConfirm: !!pinPosition && !isPinLoading,
            isLoading: isPinLoading || isConfirmingPin,
            pinWasMoved,
        });
    }, [
        showPinConfirmation,
        pinPosition,
        isPinLoading,
        isConfirmingPin,
        pinWasMoved,
        onPinConfirmationStateChange,
    ]);

    useEffect(() => {
        setLocalRoofData(roofData);
        setShowPinConfirmation(!quoteSent && !roofData?.pin_confirmed_at);
        setPinWasMoved(false);
        setPinError(null);
        setPinWarning(null);

        if (roofData?.pin_latitude != null && roofData?.pin_longitude != null) {
            const confirmedPin = {
                lat: Number(roofData.pin_latitude),
                lng: Number(roofData.pin_longitude),
            };
            setPinPosition(confirmedPin);
            setInitialPinPosition(confirmedPin);
        } else {
            setPinPosition(null);
            setInitialPinPosition(null);
        }
    }, [roofData, quoteSent]);

    const handleYesNoChange = (
        key: 'conservatoryIncluded' | 'garageIncluded' | 'asbestosRoof' | 'hasOutsideTap' | 'neighbourHasOutsideTap' | 'hasOutsideElectricalPoint',
        value: YesNo,
    ) => {
        const next: PreBookingAnswers = { ...answers, [key]: value };
        // Reset dependent fields when switching away from 'yes'
        if (key === 'conservatoryIncluded' && value !== 'yes') next.conservatorySize = null;
        if (key === 'garageIncluded' && value !== 'yes') next.garageSize = null;
        // The neighbour-tap follow-up only applies when the customer said No to
        // their own tap — clear it whenever they change away from No.
        if (key === 'hasOutsideTap' && value !== 'no') next.neighbourHasOutsideTap = null;
        onAnswersChange(next);
        onPersistAnswers?.(next);
    };

    // Calculate price using new formula
    const calculateFallbackPrice = () => {
        let price = BASE_PRICE;
        // Add SQM charge for roofs over threshold
        if (sqm > SQM_THRESHOLD) {
            price += (sqm - SQM_THRESHOLD) * SQM_RATE;
        }
        // Add extra face charge (beyond 2 standard faces)
        if (faceCount > 2) {
            price += (faceCount - 2) * EXTRA_FACE_CHARGE;
        }
        price += conservatoryExtra;
        price += garageExtra;
        price += cherryPickerExtra;
        // Apply area affluence multiplier
        price = Math.round(price * multiplier);
        return price;
    };

    useEffect(() => {
        let cancelled = false;
        setBackendPricePreview(null);

        ApiService.calculateRoofPricePreview({
            customer_id: customerData?.id,
            square_meter: sqm,
            face_count: faceCount,
            conservatory_included: answers.conservatoryIncluded === 'yes',
            conservatory_size: answers.conservatorySize,
            garage_included: answers.garageIncluded === 'yes',
            garage_size: answers.garageSize,
            stories: answers.stories,
            days_to_complete: cherryPickerDays,
            price_multiplier: multiplier,
        }).then((preview: RoofPricePreview) => {
            if (!cancelled && typeof preview?.recommended_price === 'number') {
                setBackendPricePreview(preview);
            }
        }).catch(() => {
            if (!cancelled) setBackendPricePreview(null);
        });

        return () => {
            cancelled = true;
        };
    }, [
        customerData?.id,
        sqm,
        faceCount,
        answers.conservatoryIncluded,
        answers.conservatorySize,
        answers.garageIncluded,
        answers.garageSize,
        answers.stories,
        cherryPickerDays,
        multiplier,
    ]);

    const fallbackCalculatedPrice = calculateFallbackPrice();
    const calculatedPrice = backendPricePreview?.recommended_price ?? fallbackCalculatedPrice;

    useEffect(() => {
        onSuggestedPriceChange?.(calculatedPrice);
        const previousAutoSyncedPrice = lastAutoSyncedPrice.current;
        const currentNumericPrice = Number(currentPrice) || 0;
        const isReferralDiscountedPreview = Boolean(
            (backendPricePreview?.discount_percent ?? referralDiscountPercent ?? 0) > 0
        );
        const shouldSyncSuggestedPrice =
            previousAutoSyncedPrice === null
                ? currentNumericPrice === 0 || (isReferralDiscountedPreview && !roofPriceManuallyEdited)
                : currentNumericPrice === previousAutoSyncedPrice;

        lastAutoSyncedPrice.current = calculatedPrice;

        if (shouldSyncSuggestedPrice && currentNumericPrice !== calculatedPrice) {
            onPriceChange(calculatedPrice.toString(), 'auto');
        }
    }, [backendPricePreview, calculatedPrice, currentPrice, onPriceChange, onSuggestedPriceChange, referralDiscountPercent, roofPriceManuallyEdited]);

    const floorPrice = backendPricePreview?.floor_price ?? Math.round(calculatedPrice * 0.95);
    const upperPrice = backendPricePreview?.upper_price ?? Math.round(calculatedPrice * 1.10);
    const backendBreakdown = backendPricePreview?.breakdown || {};
    const effectiveDiscountPercent = backendPricePreview?.discount_percent ?? referralDiscountPercent ?? 0;
    const effectiveDiscountAmount = backendPricePreview?.discount_amount ?? 0;
    const originalRecommendedPrice = backendPricePreview?.original_recommended_price;
    const effectiveBasePrice = backendBreakdown.base_price ?? BASE_PRICE;
    const effectiveSqmThreshold = backendBreakdown.sqm_threshold ?? SQM_THRESHOLD;
    const effectiveSqmRate = backendBreakdown.sqm_rate ?? SQM_RATE;
    const effectiveSqmAdjustment = backendBreakdown.sqm_adjustment ?? (sqm > SQM_THRESHOLD ? (sqm - SQM_THRESHOLD) * SQM_RATE : 0);
    const effectiveExtraFaces = backendBreakdown.extra_faces ?? Math.max(0, faceCount - 2);
    const effectiveExtraFaceCharge = backendBreakdown.extra_face_charge ?? EXTRA_FACE_CHARGE;
    const effectiveExtraFacesCharge = backendBreakdown.extra_faces_charge ?? effectiveExtraFaces * effectiveExtraFaceCharge;
    const effectiveConservatoryExtra = backendBreakdown.conservatory_charge ?? conservatoryExtra;
    const effectiveGarageExtra = backendBreakdown.garage_charge ?? garageExtra;
    const effectiveCherryPickerExtra = backendBreakdown.cherry_picker_charge ?? cherryPickerExtra;
    const effectiveCherryPickerRate = backendBreakdown.cherry_picker_rate_per_day ?? CHERRY_PICKER_RATE_PER_DAY;
    const effectiveCherryPickerDays = backendBreakdown.cherry_picker_days ?? cherryPickerDays;
    const effectiveMultiplier = backendBreakdown.price_multiplier ?? multiplier;

    // Local text state for the "How many storeys" input so the user can clear the
    // field while typing. Starts empty when the lead hasn't been answered yet —
    // we deliberately do NOT default to 2, so the sales rep has to type a real value.
    const [storiesInput, setStoriesInput] = useState<string>(
        answers.stories === null ? '' : String(answers.stories)
    );
    useEffect(() => {
        // Keep the local input in sync when the answers object is replaced from
        // the outside (e.g. a lead is loaded / reset). We don't fight the user
        // while they're typing because this only fires when the committed answer
        // actually differs from what we're currently showing.
        const committed = answers.stories === null ? '' : String(answers.stories);
        const typedAsNumber = parseInt(storiesInput, 10);
        const typedMatchesCommitted =
            answers.stories !== null && !isNaN(typedAsNumber) && typedAsNumber === answers.stories;
        if (!typedMatchesCommitted && committed !== storiesInput) {
            setStoriesInput(committed);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [answers.stories]);

    const GOOGLE_MAPS_API_KEY = "AIzaSyALxoDcrPSsRiTz6mV3gphXNojkPX9kDFk";

    const { isLoaded, loadError } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        libraries,
    });

    useEffect(() => {
        if (!showPinConfirmation || !isLoaded || pinPosition || !customerData?.street_address) return;
        if (!window.google || !window.google.maps) return;

        setIsPinLoading(true);
        setPinError(null);
        const geocoder = new window.google.maps.Geocoder();
        const address = [customerData.street_address, customerData.postcode].filter(Boolean).join(', ');
        geocoder.geocode({ address }, (results, status) => {
            setIsPinLoading(false);
            if (status === 'OK' && results && results[0]) {
                const location = results[0].geometry.location;
                const nextPin = { lat: location.lat(), lng: location.lng() };
                setPinPosition(nextPin);
                setInitialPinPosition(nextPin);
                setPinWasMoved(false);
                return;
            }
            setPinError(`Could not find the address on Google Maps: ${status}`);
        });
    }, [showPinConfirmation, isLoaded, pinPosition, customerData?.street_address, customerData?.postcode]);

    if (!effectiveRoofData || !customerData) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <Stack spacing={2} alignItems="center">
                    <JoyCircularProgress />
                    <Typography>Loading roof information...</Typography>
                </Stack>
            </Box>
        );
    }

    const handlePinMove = (nextPin: { lat: number; lng: number }) => {
        setPinPosition(nextPin);
        setPinWasMoved(true);
        setPinError(null);
        setPinWarning(null);
    };

    const handleConfirmPin = async () => {
        if (!effectiveRoofData || !pinPosition) {
            setPinError('Please place the pin on the customer roof first.');
            return;
        }

        setIsConfirmingPin(true);
        setPinError(null);
        setPinWarning(null);
        try {
            const result = await ApiService.confirmCustomerRoofPin(effectiveRoofData.id, {
                latitude: pinPosition.lat,
                longitude: pinPosition.lng,
                recalculate: pinWasMoved,
            });

            if (result?.roof) {
                setLocalRoofData(result.roof);
                onRoofUpdated?.(result.roof);
                setInitialPinPosition(pinPosition);
                onSquareMeterChange(String(result.roof.square_meter ?? 0));
            }
            if (result?.recalculated && typeof result?.recommended_price === 'number') {
                onPriceChange(String(result.recommended_price), 'suggested');
            }
            if (result?.recalculation_warning) {
                setPinWarning(result.recalculation_warning);
            }
            setPinWasMoved(false);
            setConfirmPinModalOpen(false);
            setShowPinConfirmation(false);
        } catch (error: any) {
            setPinError(error?.response?.data?.error || error?.message || 'Failed to confirm roof pin.');
        } finally {
            setIsConfirmingPin(false);
        }
    };

    const generateGoogleMapsLink = () => {
        if (!customerData) return "#";
        const addressQuery = encodeURIComponent(customerData.street_address);
        return `https://www.google.com/maps/search/?api=1&query=${addressQuery}`;
    };

    const handleUseSuggestedPrice = () => {
        onPriceChange(calculatedPrice.toString(), 'suggested');
    };

    const currentImageUrl = effectiveRoofData.roof_url;

    if (showPinConfirmation) {
        return (
            <Box sx={{ mx: 'auto' }}>
                <Card variant="outlined" sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography level="title-md" sx={{ mb: 1 }}>Confirm Roof Location</Typography>
                        <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                            Check the red pin is on the customer's main roof. Click the map or drag the pin if it is in the garden, road, trees, or a neighbouring property.
                        </Typography>
                        <Typography level="body-md" sx={{ mb: 1 }}>{customerData.street_address}</Typography>
                        <MuiLink
                            href={generateGoogleMapsLink()}
                            target="_blank"
                            rel="noopener noreferrer"
                            level="body-sm"
                        >
                            Open address in Google Maps
                        </MuiLink>
                    </CardContent>
                </Card>

                <Card variant="outlined">
                    <CardContent>
                        <Box
                            sx={{
                                height: '60vh',
                                minHeight: 420,
                                bgcolor: 'background.level1',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 'md',
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mb: 2,
                                position: 'relative',
                            }}
                        >
                            {loadError ? (
                                <Typography color="danger">Error loading Google Maps</Typography>
                            ) : !isLoaded || isPinLoading ? (
                                <Stack spacing={2} alignItems="center">
                                    <JoyCircularProgress />
                                    <Typography>Finding the customer address...</Typography>
                                </Stack>
                            ) : pinError && !pinPosition ? (
                                <Stack spacing={1} alignItems="center" sx={{ p: 2, textAlign: 'center' }}>
                                    <Typography color="danger">{pinError}</Typography>
                                </Stack>
                            ) : (
                                <GoogleMap
                                    id={`roof-pin-map-${customerData.id}`}
                                    mapContainerStyle={{ width: '100%', height: '100%' }}
                                    center={pinPosition || { lat: 0, lng: 0 }}
                                    zoom={20}
                                    options={{
                                        mapTypeId: 'satellite',
                                        streetViewControl: false,
                                        fullscreenControl: true,
                                        mapTypeControl: true,
                                        zoomControl: true,
                                    }}
                                    onClick={(event) => {
                                        if (!event.latLng) return;
                                        handlePinMove({ lat: event.latLng.lat(), lng: event.latLng.lng() });
                                    }}
                                >
                                    {pinPosition && (
                                        <MarkerF
                                            position={pinPosition}
                                            draggable
                                            onDragEnd={(event) => {
                                                if (!event.latLng) return;
                                                handlePinMove({ lat: event.latLng.lat(), lng: event.latLng.lng() });
                                            }}
                                        />
                                    )}
                                </GoogleMap>
                            )}
                        </Box>

                        {pinError && pinPosition && (
                            <Typography level="body-sm" color="danger" sx={{ mb: 1 }}>
                                {pinError}
                            </Typography>
                        )}

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
                            <Typography level="body-sm" color={pinWasMoved ? 'warning' : 'neutral'}>
                                {pinWasMoved
                                    ? 'Pin moved. We will recalculate the roof quote before showing the details.'
                                    : 'If the pin is already correct, use the Confirm Location button below to continue without recalculating.'}
                            </Typography>
                        </Stack>
                    </CardContent>
                </Card>

                <Modal open={confirmPinModalOpen} onClose={() => !isConfirmingPin && setConfirmPinModalOpen(false)}>
                    <ModalDialog>
                        <DialogTitle>Use this roof location?</DialogTitle>
                        <DialogContent>
                            <Stack spacing={1}>
                                <Typography level="body-sm">
                                    This pin will be saved for the roof quote.
                                </Typography>
                                {pinWasMoved && (
                                    <Typography level="body-sm" color="warning">
                                        Because the pin was moved, the roof area, face count, and price will be recalculated before the quote details are shown.
                                    </Typography>
                                )}
                                {pinPosition && (
                                    <Typography level="body-xs" color="neutral">
                                        Latitude: {pinPosition.lat.toFixed(6)}, Longitude: {pinPosition.lng.toFixed(6)}
                                    </Typography>
                                )}
                            </Stack>
                        </DialogContent>
                        <DialogActions>
                            <Button variant="plain" color="neutral" disabled={isConfirmingPin} onClick={() => setConfirmPinModalOpen(false)}>
                                Cancel
                            </Button>
                            <Button color="primary" loading={isConfirmingPin} onClick={handleConfirmPin}>
                                Confirm
                            </Button>
                        </DialogActions>
                    </ModalDialog>
                </Modal>
            </Box>
        );
    }

    return (
        <Box sx={{ mx: 'auto' }}>
            {pinWarning && (
                <Card variant="soft" color="warning" sx={{ mb: 3, border: '1px solid', borderColor: 'warning.400' }}>
                    <CardContent>
                        <Typography level="title-md" color="warning" sx={{ mb: 0.75 }}>
                            Manual roof quote review required
                        </Typography>
                        <Typography level="body-sm">
                            {pinWarning}
                        </Typography>
                    </CardContent>
                </Card>
            )}

            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start', mb: 1 }}>
                        <Box>
                            <Typography level="title-sm" sx={{ mb: 1 }}>📍 Property Address</Typography>
                            <Typography level="body-lg">{customerData.street_address}</Typography>
                        </Box>
                        <Button
                            size="sm"
                            variant="outlined"
                            onClick={() => {
                                setShowPinConfirmation(true);
                                setPinWasMoved(false);
                                setPinError(null);
                            }}
                        >
                            Recalculate
                        </Button>
                    </Box>
                    <MuiLink
                        href={generateGoogleMapsLink()}
                        target="_blank"
                        rel="noopener noreferrer"
                        level="body-sm"
                    >
                        View on Google Maps →
                    </MuiLink>
                </CardContent>
            </Card>

            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Typography level="title-sm">Roof Quote Image</Typography>
                    <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                        This satellite image is generated from the confirmed roof pin.
                    </Typography>

                    <Box
                        sx={{
                            p: 1,
                            border: currentImageUrl ? '1px solid' : '2px dashed',
                            borderColor: 'divider',
                            borderRadius: 'md',
                            textAlign: 'center',
                            bgcolor: 'background.level1',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '300px'
                        }}
                    >
                        {currentImageUrl ? (
                            <img
                                src={currentImageUrl}
                                alt="Roof"
                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                            />
                        ) : (
                            <span style={{ color: '#777' }}>Confirm the roof pin to generate an image</span>
                        )}
                    </Box>
                </CardContent>
            </Card>

            <Divider sx={{ my: 3 }} />

            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Typography level="title-md" sx={{ mb: 3 }}>Quote Details</Typography>

                    {/* Roof Data Section */}
                    <Box sx={{ mb: 3, p: 2, bgcolor: 'background.level1', borderRadius: 'sm' }}>
                        <Typography level="title-sm" sx={{ mb: 2 }}>Roof Information</Typography>
                        <Stack direction="row" spacing={3} flexWrap="wrap">
                            <Box>
                                <Typography level="body-xs" color="neutral">Face Count</Typography>
                                <Typography level="body-lg" fontWeight="bold">{faceCount}</Typography>
                            </Box>
                            <Box>
                                <Typography level="body-xs" color="neutral">Square Meters</Typography>
                                <Typography level="body-lg" fontWeight="bold">{sqm} m²</Typography>
                            </Box>
                            <Box>
                                <Typography level="body-xs" color="neutral">Area Multiplier</Typography>
                                <Typography level="body-lg" fontWeight="bold">{multiplier}x</Typography>
                            </Box>
                        </Stack>
                    </Box>

                    {/* Pre-Booking Questions */}
                    <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography level="title-sm">Pre-Booking Questions</Typography>
                            {missingAnswers.length === 0 ? (
                                <Typography level="body-xs" color="success" fontWeight="bold">All answered ✓</Typography>
                            ) : (
                                <Typography level="body-xs" color={showValidation ? 'danger' : 'warning'} fontWeight="bold">
                                    {missingAnswers.length} question{missingAnswers.length === 1 ? '' : 's'} remaining
                                </Typography>
                            )}
                        </Box>
                        <Typography level="body-xs" color="neutral" sx={{ mb: 2 }}>
                            All questions must be answered before the booking can be created.
                        </Typography>

                        {/* Q1: Asbestos Roof (first — this is a hard blocker) */}
                        <Box sx={questionBoxSx((showValidation && asbestosMissing) || answers.asbestosRoof === 'yes')}>
                            <Typography level="body-md" color={(showValidation && asbestosMissing) || answers.asbestosRoof === 'yes' ? 'danger' : undefined}>
                                Asbestos Roof?
                            </Typography>
                            <Typography level="body-xs" color="neutral" sx={{ mb: 1 }}>
                                Ask the customer: "Do you know if your roof has asbestos in it?"
                            </Typography>
                            <RadioGroup
                                orientation="horizontal"
                                value={answers.asbestosRoof ?? ''}
                                onChange={(e) => handleYesNoChange('asbestosRoof', (e.target.value || null) as YesNo)}
                            >
                                <Radio value="yes" label="Yes" color={(showValidation && asbestosMissing) || answers.asbestosRoof === 'yes' ? 'danger' : 'neutral'} />
                                <Radio value="no" label="No" color={showValidation && asbestosMissing ? 'danger' : 'neutral'} />
                            </RadioGroup>
                            {showValidation && asbestosMissing && (
                                <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>
                                    Please answer this question.
                                </Typography>
                            )}
                            {answers.asbestosRoof === 'yes' && (
                                <Box sx={{ mt: 1.5, p: 1.5, border: '1px solid', borderColor: 'danger.500', borderRadius: 'sm', bgcolor: 'danger.softBg' }}>
                                    <Typography level="body-sm" color="danger" fontWeight="bold" sx={{ mb: 0.5 }}>
                                        🚫 Cannot proceed with booking
                                    </Typography>
                                    <Typography level="body-xs" color="danger">
                                        {ASBESTOS_BLOCK_MESSAGE}
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {/* Q2: Include External Garage */}
                        <Box sx={questionBoxSx(showValidation && (garageMissing || garageSizeMissing))}>
                            <Typography level="body-md" color={showValidation && garageMissing ? 'danger' : undefined}>
                                Include External Garage (and do they want it cleaned)?
                            </Typography>
                            <Typography level="body-xs" color="neutral" sx={{ mb: 1 }}>
                                Ask the customer: "Do you have a garage that's detached from the main house, and do you want it cleaned if so?"
                            </Typography>
                            <RadioGroup
                                orientation="horizontal"
                                value={answers.garageIncluded ?? ''}
                                onChange={(e) => handleYesNoChange('garageIncluded', (e.target.value || null) as YesNo)}
                            >
                                <Radio value="yes" label="Yes" color={showValidation && garageMissing ? 'danger' : 'neutral'} />
                                <Radio value="no" label="No" color={showValidation && garageMissing ? 'danger' : 'neutral'} />
                            </RadioGroup>
                            {showValidation && garageMissing && (
                                <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>
                                    Please answer this question.
                                </Typography>
                            )}
                            {answers.garageIncluded === 'yes' && (
                                <Box sx={{ mt: 1.5 }}>
                                    <FormControl error={showValidation && garageSizeMissing}>
                                        <FormLabel>External Garage Size</FormLabel>
                                        <Select
                                            placeholder="Select size"
                                            color={showValidation && garageSizeMissing ? 'danger' : 'neutral'}
                                            value={answers.garageSize ?? ''}
                                            onChange={(_, value) => updateAnswer('garageSize', (value || null) as PreBookingSize | null)}
                                        >
                                            <Option value="small">Small (+£{GARAGE_SIZE_PRICES.small})</Option>
                                            <Option value="medium">Medium (+£{GARAGE_SIZE_PRICES.medium})</Option>
                                            <Option value="large">Large (+£{GARAGE_SIZE_PRICES.large})</Option>
                                        </Select>
                                        {showValidation && garageSizeMissing && (
                                            <FormHelperText>
                                                <Typography level="body-xs" color="danger">Please select a size.</Typography>
                                            </FormHelperText>
                                        )}
                                    </FormControl>
                                </Box>
                            )}
                        </Box>

                        {/* Q3: Include Conservatory */}
                        <Box sx={questionBoxSx(showValidation && (conservatoryMissing || conservatorySizeMissing))}>
                            <Typography level="body-md" color={showValidation && conservatoryMissing ? 'danger' : undefined}>
                                Include Conservatory (and do they want it cleaned)?
                            </Typography>
                            <Typography level="body-xs" color="neutral" sx={{ mb: 1 }}>
                                Ask the customer: "Do you have a conservatory, and do you want it cleaned if so?"
                            </Typography>
                            <RadioGroup
                                orientation="horizontal"
                                value={answers.conservatoryIncluded ?? ''}
                                onChange={(e) => handleYesNoChange('conservatoryIncluded', (e.target.value || null) as YesNo)}
                            >
                                <Radio value="yes" label="Yes" color={showValidation && conservatoryMissing ? 'danger' : 'neutral'} />
                                <Radio value="no" label="No" color={showValidation && conservatoryMissing ? 'danger' : 'neutral'} />
                            </RadioGroup>
                            {showValidation && conservatoryMissing && (
                                <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>
                                    Please answer this question.
                                </Typography>
                            )}
                            {answers.conservatoryIncluded === 'yes' && (
                                <Box sx={{ mt: 1.5 }}>
                                    <FormControl error={showValidation && conservatorySizeMissing}>
                                        <FormLabel>Conservatory Size</FormLabel>
                                        <Select
                                            placeholder="Select size"
                                            color={showValidation && conservatorySizeMissing ? 'danger' : 'neutral'}
                                            value={answers.conservatorySize ?? ''}
                                            onChange={(_, value) => updateAnswer('conservatorySize', (value || null) as PreBookingSize | null)}
                                        >
                                            <Option value="small">Small (+£{CONSERVATORY_SIZE_PRICES.small})</Option>
                                            <Option value="medium">Medium (+£{CONSERVATORY_SIZE_PRICES.medium})</Option>
                                            <Option value="large">Large (+£{CONSERVATORY_SIZE_PRICES.large})</Option>
                                        </Select>
                                        {showValidation && conservatorySizeMissing && (
                                            <FormHelperText>
                                                <Typography level="body-xs" color="danger">Please select a size.</Typography>
                                            </FormHelperText>
                                        )}
                                    </FormControl>
                                </Box>
                            )}
                        </Box>

                        {/* Q4: Number of storeys */}
                        <Box sx={{
                            mb: 2,
                            p: 2,
                            border: '1px solid',
                            borderColor: (showValidation && storiesMissing)
                                ? 'danger.500'
                                : cherryPickerRequired ? 'warning.500' : 'divider',
                            borderRadius: 'sm',
                            bgcolor: (showValidation && storiesMissing)
                                ? 'danger.softBg'
                                : cherryPickerRequired ? 'warning.softBg' : undefined,
                        }}>
                            <Typography level="body-md" color={showValidation && storiesMissing ? 'danger' : undefined}>
                                How many storeys is the house?
                            </Typography>
                            <Typography level="body-xs" color="neutral" sx={{ mb: 1 }}>
                                Ask the customer: "How many storeys is your house?" (A 3+ storey house needs a cherry picker.)
                            </Typography>
                            <FormControl error={showValidation && storiesMissing}>
                                <Input
                                    type="number"
                                    placeholder="e.g. 2"
                                    value={storiesInput}
                                    color={showValidation && storiesMissing ? 'danger' : 'neutral'}
                                    slotProps={{ input: { min: 1, max: 10, step: 1, inputMode: 'numeric' } }}
                                    onChange={(e) => {
                                        const raw = e.target.value;
                                        setStoriesInput(raw);
                                        if (raw === '') {
                                            // Clearing the input resets the answer so validation
                                            // picks it up as unanswered.
                                            if (answers.stories !== null) updateAnswer('stories', null);
                                            return;
                                        }
                                        const n = parseInt(raw, 10);
                                        if (!isNaN(n) && n > 0) {
                                            updateAnswer('stories', n);
                                        }
                                    }}
                                    onBlur={() => {
                                        const n = parseInt(storiesInput, 10);
                                        if (storiesInput === '' || isNaN(n) || n <= 0) {
                                            // Intentionally do NOT snap to 2 — we want the
                                            // sales rep to actively answer this question.
                                            setStoriesInput('');
                                            if (answers.stories !== null) updateAnswer('stories', null);
                                        } else {
                                            setStoriesInput(String(n));
                                            if (n !== answers.stories) updateAnswer('stories', n);
                                        }
                                    }}
                                    sx={{ maxWidth: 140 }}
                                />
                                {showValidation && storiesMissing && (
                                    <FormHelperText>
                                        <Typography level="body-xs" color="danger">
                                            Please enter the number of storeys.
                                        </Typography>
                                    </FormHelperText>
                                )}
                            </FormControl>
                            {cherryPickerRequired && (
                                <Box sx={{ mt: 1.5, p: 1.5, border: '1px solid', borderColor: 'warning.400', borderRadius: 'sm', bgcolor: 'background.surface' }}>
                                    <Typography level="body-sm" color="warning" fontWeight="bold" sx={{ mb: 0.5 }}>
                                        ⚠️ Cherry picker required
                                    </Typography>
                                    <Typography level="body-xs">
                                        Tell the customer that a cherry picker will need to be hired for this job, which costs an extra £{CHERRY_PICKER_RATE_PER_DAY} per day. Estimated days: <b>{cherryPickerDays}</b>, so <b>+£{cherryPickerExtra}</b> will be added to the roof clean price.
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {/* Q5: Outside tap (water access) */}
                        <Box sx={questionBoxSx((showValidation && (outsideTapMissing || neighbourTapMissing)) || isNoWaterBlocked(answers))}>
                            <Typography level="body-md" color={showValidation && outsideTapMissing ? 'danger' : undefined}>
                                Do they have an outside tap?
                            </Typography>
                            <Typography level="body-xs" color="neutral" sx={{ mb: 1 }}>
                                Ask the customer: "Do you have an outside tap we can use for water access on the day?"
                            </Typography>
                            <RadioGroup
                                orientation="horizontal"
                                value={answers.hasOutsideTap ?? ''}
                                onChange={(e) => handleYesNoChange('hasOutsideTap', (e.target.value || null) as YesNo)}
                            >
                                <Radio value="yes" label="Yes" color={showValidation && outsideTapMissing ? 'danger' : 'neutral'} />
                                <Radio value="no" label="No" color={showValidation && outsideTapMissing ? 'danger' : 'neutral'} />
                            </RadioGroup>
                            {showValidation && outsideTapMissing && (
                                <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>
                                    Please answer this question.
                                </Typography>
                            )}
                            {answers.hasOutsideTap === 'no' && (
                                <Box sx={{ mt: 1.5, pl: 1.5, borderLeft: '2px solid', borderColor: showValidation && neighbourTapMissing ? 'danger.500' : 'neutral.outlinedBorder' }}>
                                    <Typography level="body-md" color={showValidation && neighbourTapMissing ? 'danger' : undefined}>
                                        Does a neighbour have an outside tap we could use?
                                    </Typography>
                                    <Typography level="body-xs" color="neutral" sx={{ mb: 1 }}>
                                        Ask the customer: "Does a neighbour either side have an outside tap, and can you get their direct permission for us to use it on the day?" A client saying the neighbour has a tap is not enough - the neighbour must directly confirm because they would pay for any additional water usage.
                                    </Typography>
                                    <RadioGroup
                                        orientation="horizontal"
                                        value={answers.neighbourHasOutsideTap ?? ''}
                                        onChange={(e) => handleYesNoChange('neighbourHasOutsideTap', (e.target.value || null) as YesNo)}
                                    >
                                        <Radio value="yes" label="Yes" color={showValidation && neighbourTapMissing ? 'danger' : 'neutral'} />
                                        <Radio value="no" label="No" color={showValidation && neighbourTapMissing ? 'danger' : 'neutral'} />
                                    </RadioGroup>
                                    {showValidation && neighbourTapMissing && (
                                        <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>
                                            Please answer this question.
                                        </Typography>
                                    )}
                                    {answers.neighbourHasOutsideTap === 'no' && (
                                        <Box sx={{ mt: 1.5, p: 1.5, border: '1px solid', borderColor: 'danger.500', borderRadius: 'sm', bgcolor: 'danger.softBg' }}>
                                            <Typography level="body-sm" color="danger" fontWeight="bold" sx={{ mb: 0.5 }}>
                                                🚫 Cannot proceed with booking
                                            </Typography>
                                            <Typography level="body-xs" color="danger">
                                                {NO_WATER_BLOCK_MESSAGE}
                                            </Typography>
                                        </Box>
                                    )}
                                    {answers.neighbourHasOutsideTap === 'yes' && (
                                        <Typography level="body-xs" color="success" sx={{ mt: 1 }}>
                                            Only proceed once the neighbour has directly confirmed permission to use their tap. Client confirmation alone is not enough.
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </Box>

                        {/* Q6: Outside electrical point */}
                        <Box sx={questionBoxSx(showValidation && electricalPointMissing)}>
                            <Typography level="body-md" color={showValidation && electricalPointMissing ? 'danger' : undefined}>
                                Do they have an outside electrical point?
                            </Typography>
                            <Typography level="body-xs" color="neutral" sx={{ mb: 1 }}>
                                Ask the customer: "Do you have an outside electrical socket we can use on the day?"
                            </Typography>
                            <RadioGroup
                                orientation="horizontal"
                                value={answers.hasOutsideElectricalPoint ?? ''}
                                onChange={(e) => handleYesNoChange('hasOutsideElectricalPoint', (e.target.value || null) as YesNo)}
                            >
                                <Radio value="yes" label="Yes" color={showValidation && electricalPointMissing ? 'danger' : 'neutral'} />
                                <Radio value="no" label="No" color={showValidation && electricalPointMissing ? 'danger' : 'neutral'} />
                            </RadioGroup>
                            {showValidation && electricalPointMissing && (
                                <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>
                                    Please answer this question.
                                </Typography>
                            )}
                            {answers.hasOutsideElectricalPoint === 'no' && (
                                <Typography level="body-xs" color="warning" sx={{ mt: 1 }}>
                                    Note this for the van team — they may need to bring an alternative power source.
                                </Typography>
                            )}
                        </Box>

                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <FormControl sx={{ mb: 3 }} error={!!error}>
                        <FormLabel>Roof Size (Square Meters)</FormLabel>
                        <Input
                            type="number"
                            value={squareMeter}
                            onChange={(e) => onSquareMeterChange(e.target.value)}
                            slotProps={{ input: { min: "0", step: "0.01" } }}
                            size="md"
                            fullWidth
                        />
                        {error && (
                            <FormHelperText>
                                <Typography color="danger">{error}</Typography>
                            </FormHelperText>
                        )}
                    </FormControl>

                    <FormControl sx={{ mb: 3 }}>
                        <FormLabel>Estimated Days to Complete</FormLabel>
                        <Input
                            type="text"
                            value={
                                daysRange
                                    ? daysRange.min === daysRange.max
                                        ? `${daysRange.min} day${daysRange.min !== 1 ? 's' : ''}`
                                        : `${daysRange.min} – ${daysRange.max} days`
                                    : '–'
                            }
                            disabled
                            size="md"
                            fullWidth
                        />
                        <FormHelperText>
                            {daysRange && daysRange.min !== daysRange.max
                                ? 'Range based on van capabilities. Final value set when a van is assigned.'
                                : 'Will be set when a van is assigned. Can be changed from the booking page.'}
                        </FormHelperText>
                    </FormControl>

                    {/* Price Breakdown */}
                    <Box sx={{ mb: 3, p: 2, bgcolor: 'background.level2', borderRadius: 'sm' }}>
                        <Typography level="title-sm" sx={{ mb: 1 }}>Price Breakdown</Typography>
                        <Stack spacing={0.5}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography level="body-sm">Base price</Typography>
                                <Typography level="body-sm">£{effectiveBasePrice}</Typography>
                            </Box>
                            {effectiveSqmAdjustment > 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography level="body-sm">SQM charge ({sqm - effectiveSqmThreshold} m² over {effectiveSqmThreshold})</Typography>
                                    <Typography level="body-sm">+£{effectiveSqmAdjustment}</Typography>
                                </Box>
                            )}
                            {effectiveExtraFaces > 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography level="body-sm">Extra faces ({effectiveExtraFaces} × £{effectiveExtraFaceCharge})</Typography>
                                    <Typography level="body-sm">+£{effectiveExtraFacesCharge}</Typography>
                                </Box>
                            )}
                            {effectiveConservatoryExtra > 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography level="body-sm">Conservatory ({formatSize(answers.conservatorySize)})</Typography>
                                    <Typography level="body-sm">+£{effectiveConservatoryExtra}</Typography>
                                </Box>
                            )}
                            {effectiveGarageExtra > 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography level="body-sm">External Garage ({formatSize(answers.garageSize)})</Typography>
                                    <Typography level="body-sm">+£{effectiveGarageExtra}</Typography>
                                </Box>
                            )}
                            {effectiveCherryPickerExtra > 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography level="body-sm" color="warning">
                                        Cherry picker hire ({answers.stories ?? 0} storeys, £{effectiveCherryPickerRate}/day × {effectiveCherryPickerDays} day{effectiveCherryPickerDays === 1 ? '' : 's'})
                                    </Typography>
                                    <Typography level="body-sm" color="warning">+£{effectiveCherryPickerExtra}</Typography>
                                </Box>
                            )}
                            {effectiveMultiplier !== 1.0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography level="body-sm">Area multiplier</Typography>
                                    <Typography level="body-sm">×{effectiveMultiplier}</Typography>
                                </Box>
                            )}
                            {effectiveDiscountPercent > 0 && effectiveDiscountAmount > 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        <Typography level="body-sm">Referral discount ({effectiveDiscountPercent}%)</Typography>
                                        <Chip color="primary" variant="soft" size="sm">Referral Discount</Chip>
                                    </Box>
                                    <Typography level="body-sm" color="primary">-£{effectiveDiscountAmount}</Typography>
                                </Box>
                            )}
                            <Divider sx={{ my: 1 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography level="body-md" fontWeight="bold">Calculated Total</Typography>
                                <Typography level="body-md" fontWeight="bold">£{calculatedPrice}</Typography>
                            </Box>
                        </Stack>
                    </Box>

                    <Card variant="soft" color="primary" sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography level="title-sm" sx={{ mb: 2 }}>Pricing Guide</Typography>
                            <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="stretch">
                                <Box onClick={() => onPriceChange(floorPrice.toString(), 'manual')} sx={{ flex: 1, textAlign: 'center', p: 1.5, bgcolor: 'background.surface', borderRadius: 'sm', border: '1px solid', borderColor: currentPrice === floorPrice ? 'warning.400' : 'neutral.200', cursor: 'pointer', '&:hover': { borderColor: 'warning.400', bgcolor: 'warning.softBg' }, transition: 'all 0.15s' }}>
                                    <Typography level="body-xs" color="neutral" fontWeight="bold" textTransform="uppercase">Minimum</Typography>
                                    <Typography level="h4" fontWeight="bold" color="warning">£{floorPrice}</Typography>
                                    <Typography level="body-xs" color="neutral">-5% from recommended</Typography>
                                </Box>
                                <Box onClick={() => onPriceChange(calculatedPrice.toString(), 'suggested')} sx={{ flex: 1, textAlign: 'center', p: 1.5, bgcolor: 'background.surface', borderRadius: 'sm', border: '2px solid', borderColor: currentPrice === calculatedPrice ? 'success.500' : 'success.400', cursor: 'pointer', '&:hover': { borderColor: 'success.500', bgcolor: 'success.softBg' }, transition: 'all 0.15s' }}>
                                    <Typography level="body-xs" color="success" fontWeight="bold" textTransform="uppercase">Recommended</Typography>
                                    <Typography level="h4" fontWeight="bold" color="success">£{calculatedPrice}</Typography>
                                    <Typography level="body-xs" color="neutral">With {effectiveMultiplier}x area adj.</Typography>
                                </Box>
                                <Box onClick={() => onPriceChange(upperPrice.toString(), 'manual')} sx={{ flex: 1, textAlign: 'center', p: 1.5, bgcolor: 'background.surface', borderRadius: 'sm', border: '1px solid', borderColor: currentPrice === upperPrice ? 'primary.400' : 'neutral.200', cursor: 'pointer', '&:hover': { borderColor: 'primary.400', bgcolor: 'primary.softBg' }, transition: 'all 0.15s' }}>
                                    <Typography level="body-xs" color="neutral" fontWeight="bold" textTransform="uppercase">Upper</Typography>
                                    <Typography level="h4" fontWeight="bold" color="neutral">£{upperPrice}</Typography>
                                    <Typography level="body-xs" color="neutral">+10% from recommended</Typography>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>

                    <FormControl>
                        <FormLabel>Final Quote Price</FormLabel>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Input
                                startDecorator="£"
                                type="number"
                                value={currentPrice.toString()}
                                onChange={(e) => onPriceChange(e.target.value, 'manual')}
                                slotProps={{ input: { min: 0, step: '0.01' } }}
                                size="md"
                                sx={{ flex: 1 }}
                            />
                        </Stack>
                        {currentPrice !== calculatedPrice && (
                            <div>
                                <Button
                                    sx={{ p: 1, m: 0 }}
                                    variant={"plain"}
                                    onClick={handleUseSuggestedPrice}
                                >
                                    Click here to use £{calculatedPrice}
                                </Button>
                            </div>
                        )}
                    </FormControl>
                </CardContent>
            </Card>
        </Box>
    );
});

export default ConfirmCustomersRoofTab;
